/**
 * useFillQuestionnaire — state and behaviour for the supplier onboarding
 * questionnaire (FillQuestionnaire.tsx).
 *
 * Extracted from the component under CR-6165. Two reasons: the page was 722
 * lines, over the repo's 500-line limit, and adding auto-save to it would have
 * pushed it further; and it now mirrors useQualificationDetail, so the two
 * questionnaire surfaces keep the same shape.
 *
 * Behaviour is unchanged apart from the CR-6165 additions, which are marked.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { onboardingAPI, FilledDatasetValue, ScorePreview } from '../../lib/api';
import { useAutoSave, AutoSaveStatus } from '../../hooks/useAutoSave';
import { getQuestionLockState } from './QuestionResubmissionWrapper';
import {
  evaluateConditionalLogic,
  getAnsweredCount,
  getMissingRequiredQuestions,
  getVisibleQuestions,
} from './questionnaireVisibility';
import { buildAnswersPayload } from './questionnairePayload';
import {
  Question,
  Section,
  FilledDatasetDetail,
  AnswerValue,
} from './questionnaireTypes';

/** Matches the qualifications page so both questionnaires feel the same. */
const AUTO_SAVE_DELAY_MS = 3000;

export function useFillQuestionnaire(id: string | undefined) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filledDataset, setFilledDataset] = useState<FilledDatasetDetail | null>(null);
  const [answers, setAnswers] = useState<Record<number, AnswerValue>>({});
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<Record<number, boolean>>({});
  const [uploadedFiles, setUploadedFiles] = useState<Record<number, { name: string; path: string }>>({});
  // Question IDs whose answers were carried forward from a prior submission.
  // Cleared when the supplier saves a new answer for that question.
  const [carriedForwardIds, setCarriedForwardIds] = useState<Set<number>>(new Set());
  // Human-readable label for the origin of carried answers (e.g., "Issue 2, 12 Mar 2026").
  const [carriedFromLabel, setCarriedFromLabel] = useState<string | undefined>(undefined);
  const [scorePreview, setScorePreview] = useState<ScorePreview | null>(null);
  const [loadingScore, setLoadingScore] = useState(false);

  // CR-6165 auto-save bookkeeping — see useQualificationDetail for the
  // reasoning behind the sequence counter.
  const editSeqRef = useRef(0);
  const dirtyIdsRef = useRef<Set<number>>(new Set());
  const [unsavedCount, setUnsavedCount] = useState(0);
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  const markDirty = useCallback((questionId: number) => {
    editSeqRef.current += 1;
    dirtyIdsRef.current.add(questionId);
    setUnsavedCount(dirtyIdsRef.current.size);
  }, []);

  useEffect(() => {
    if (id) {
      fetchFilledDataset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchFilledDataset = async () => {
    setLoading(true);
    // CR-6165: anything typed while this request is in flight is newer than
    // the server's copy, so do not let the response overwrite it.
    const editSeqAtFetchStart = editSeqRef.current;
    try {
      const response = await onboardingAPI.getFilledDataset(Number(id));
      const data = response.data as FilledDatasetDetail;
      setFilledDataset(data);

      // Initialize answers from existing values
      const existingAnswers: Record<number, AnswerValue> = {};
      const existingFiles: Record<number, { name: string; path: string }> = {};
      if (data.values) {
        data.values.forEach((value: FilledDatasetValue) => {
          const questionId = value.question;
          if (value.file_path) {
            existingAnswers[questionId] = value.file_path;
            existingFiles[questionId] = { name: value.file_path.split('/').pop() || 'file', path: value.file_path };
          } else if (value.matrix_values && Object.keys(value.matrix_values).length > 0) {
            existingAnswers[questionId] = value.matrix_values;
          } else if (value.signature_data) {
            existingAnswers[questionId] = value.signature_data;
          } else if (value.choice_values && value.choice_values.length > 0) {
            existingAnswers[questionId] = value.choice_values;
          } else if (value.boolean_value !== null && value.boolean_value !== undefined) {
            existingAnswers[questionId] = value.boolean_value;
          } else if (value.text_value) {
            // Legacy MAB data stored every answer in text_value regardless of
            // the question type. Convert yes/no strings into booleans so the
            // yes/no RadioGroup (which expects `true` / `false`) can pre-
            // select correctly. For choice-style questions, expose the string
            // value as a single-item array so single_choice / multiple_choice
            // renderers light up their matching option.
            if (value.question_type === 'yes_no') {
              const normalised = (value.text_value || '').trim().toLowerCase();
              if (normalised === 'yes' || normalised === 'true' || normalised === '1') {
                existingAnswers[questionId] = true;
              } else if (normalised === 'no' || normalised === 'false' || normalised === '0') {
                existingAnswers[questionId] = false;
              } else {
                existingAnswers[questionId] = value.text_value;
              }
            } else if (value.question_type === 'single_choice' || value.question_type === 'multiple_choice') {
              existingAnswers[questionId] = [value.text_value];
            } else {
              existingAnswers[questionId] = value.text_value;
            }
          } else if (value.number_value !== null && value.number_value !== undefined) {
            existingAnswers[questionId] = value.number_value;
          } else if (value.date_value) {
            existingAnswers[questionId] = value.date_value;
          }
        });
      }
      if (editSeqRef.current === editSeqAtFetchStart) {
        setAnswers(existingAnswers);
        setUploadedFiles(existingFiles);
      }
      setCarriedForwardIds(
        new Set((data.values ?? []).filter(v => v.carried_forward).map(v => v.question)),
      );
      const priorVersion = data.carried_forward_from_version;
      const priorDate = data.carried_forward_from_submitted_at;
      if (priorVersion || priorDate) {
        const dateStr = priorDate ? format(new Date(priorDate), 'd MMM yyyy') : null;
        setCarriedFromLabel([priorVersion, dateStr].filter(Boolean).join(', ') || undefined);
      } else {
        setCarriedFromLabel(undefined);
      }

      if (data.dataset_detail?.sections?.length) {
        setExpandedSections([`section-${data.dataset_detail.sections[0].id}`]);
      }
    } catch (error) {
      console.error('Failed to fetch filled dataset:', error);
      toast.error('Failed to load questionnaire');
      navigate('/onboarding/my-progress');
    } finally {
      setLoading(false);
    }
  };

  const fetchScorePreview = async () => {
    if (!id) return;
    setLoadingScore(true);
    try {
      const response = await onboardingAPI.previewScore(Number(id));
      setScorePreview(response.data);
    } catch (error) {
      console.error('Failed to fetch score preview:', error);
    } finally {
      setLoadingScore(false);
    }
  };

  useEffect(() => {
    if (filledDataset && !loading) {
      fetchScorePreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filledDataset?.id, loading]);

  const handleAnswerChange = useCallback((questionId: number, value: string | number | boolean | string[] | Record<string, string | string[]>) => {
    markDirty(questionId);
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    setCarriedForwardIds(prev => {
      if (!prev.has(questionId)) return prev;
      const next = new Set(prev);
      next.delete(questionId);
      return next;
    });
  }, [markDirty]);

  const handleFileUpload = async (questionId: number, file: File) => {
    if (!filledDataset) return;

    setUploadingFiles(prev => ({ ...prev, [questionId]: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('question_id', questionId.toString());
      formData.append('filled_dataset_id', filledDataset.id.toString());

      const response = await onboardingAPI.uploadQuestionFile(formData);
      const { file_path, file_name } = response.data;

      setUploadedFiles(prev => ({ ...prev, [questionId]: { name: file_name, path: file_path } }));
      setAnswers(prev => ({ ...prev, [questionId]: file_path }));
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Failed to upload file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [questionId]: false }));
    }
  };

  const handleFileRemove = (questionId: number) => {
    // Deliberately does NOT arm auto-save. bulk_save never writes file_path
    // (value_views.py builds `incoming` without it), so a background save
    // could not persist a removal — but it would still flip the pill to
    // "Saved HH:MM" and stand the leave-page guard down, telling the supplier
    // their removal was stored when the file comes back on reload. Removal
    // stays on the manual-save path, exactly as before CR-6165, and the
    // underlying backend gap is logged separately.
    setUploadedFiles(prev => {
      const updated = { ...prev };
      delete updated[questionId];
      return updated;
    });
    setAnswers(prev => ({ ...prev, [questionId]: '' }));
  };

  // Conditional logic, visibility and progress live in
  // questionnaireVisibility.ts; the payload mapping in questionnairePayload.ts.
  // Thin wrappers here keep the component's call signatures unchanged.
  const shouldShowSection = useCallback(
    (section: Section): boolean => evaluateConditionalLogic(section.conditional_logic, answers),
    [answers],
  );

  const shouldShowQuestion = useCallback(
    (question: Question): boolean => evaluateConditionalLogic(question.conditional_logic, answers),
    [answers],
  );

  const getMissingRequired = (): Question[] => getMissingRequiredQuestions(filledDataset, answers);
  const getAnswered = (): number => getAnsweredCount(filledDataset, answers);
  const getTotalQuestions = (): number => getVisibleQuestions(filledDataset, answers).length;

  const isReadOnly = filledDataset?.status === 'submitted' ||
                     filledDataset?.status === 'under_review' ||
                     filledDataset?.status === 'approved';
  const resubmissionMode = filledDataset?.status === 'more_info_required';

  /** Question ids the reviewer has already approved — locked for editing. */
  const lockedQuestionIds = useCallback((): Set<number> => {
    if (!resubmissionMode) return new Set<number>();
    const locked = new Set<number>();
    (filledDataset?.values ?? []).forEach((v) => {
      if (getQuestionLockState(v, true).locked) locked.add(v.question);
    });
    return locked;
  }, [filledDataset, resubmissionMode]);

  /**
   * CR-6165: one save path for the manual button and the auto-save.
   *
   * A silent (auto) save skips the toast and the refetch — the refetch would
   * replace `answers` from the server mid-typing — and leaves reviewer-locked
   * answers out of the payload, so a background write can never clear the
   * `carried_forward` flag on an answer the supplier cannot even edit. The
   * manual path keeps its existing behaviour exactly.
   */
  const saveAnswers = async (
    source: Record<number, AnswerValue>,
    { silent, suppressErrorToast = false }: { silent: boolean; suppressErrorToast?: boolean },
  ) => {
    if (!filledDataset) return;
    // A background save sends ONLY the answers that changed. The manual path
    // still sends the whole map, unchanged.
    //
    // Two reasons. Cost: the endpoint does a lookup plus an update_or_create
    // per entry, so re-posting a 100-question questionnaire every three
    // seconds of typing is ~200 queries a tick. Correctness: every entry in
    // the payload gets its `answered_at` bumped and is re-examined for
    // `carried_forward`, so including untouched answers churns rows the
    // supplier never went near.
    //
    // Reviewer-locked answers are excluded from background saves too — a
    // silent write must never clear `carried_forward` on an answer the
    // supplier cannot even edit.
    const skipIds = silent ? lockedQuestionIds() : new Set<number>();
    const scoped = silent
      ? Object.fromEntries(
          Object.entries(source).filter(([questionId]) => dirtyIdsRef.current.has(Number(questionId))),
        )
      : source;
    const payload = buildAnswersPayload(filledDataset, scoped, skipIds);
    // The bulk endpoint rejects an empty answers array (allow_empty=False), so
    // a background tick with nothing to send must not fire. Reachable in
    // resubmission mode, where every dirty answer can be reviewer-locked.
    // Clear the dirty flags too: there is nothing left that a save could
    // persist, and leaving them set would strand the form permanently dirty
    // with a leave-page prompt that no amount of saving could satisfy.
    if (silent && payload.length === 0) {
      dirtyIdsRef.current.clear();
      setUnsavedCount(0);
      return;
    }
    const seqAtSaveStart = editSeqRef.current;
    if (!silent) setSaving(true);
    setAutoSaveStatus('saving');
    try {
      await onboardingAPI.bulkSaveAnswers(filledDataset.id, payload);
      setAutoSaveStatus('saved');
      setLastSavedAt(new Date());
      if (editSeqRef.current === seqAtSaveStart) {
        dirtyIdsRef.current.clear();
        setUnsavedCount(0);
      }
      if (!silent) {
        toast.success('Progress saved successfully');
        await fetchFilledDataset();
      }
    } catch (error) {
      console.error('Failed to save answers:', error);
      setAutoSaveStatus('error');
      // CR-6165, deliberate behaviour change: rethrow on the manual path so
      // handleSubmit aborts. Previously handleSave swallowed its own error and
      // returned normally, so a failed save was followed by a submit anyway —
      // finalising a questionnaire whose latest answers had not been stored.
      // The toast is left to handleSubmit so the user gets one clear message
      // rather than "Failed to save" stacked on "Failed to submit".
      if (!silent) {
        if (!suppressErrorToast) toast.error('Failed to save progress');
        throw error;
      }
    } finally {
      if (!silent) setSaving(false);
    }
  };

  /**
   * The Save Progress button handler.
   *
   * Swallows the rejection that `saveAnswers` raises for handleSubmit's
   * benefit: the toast has already been shown by this point, and letting it
   * escape an onClick leaves an "Uncaught (in promise)" in the console, which
   * the repo treats as a failure.
   */
  const handleSave = async () => {
    try {
      await saveAnswers(answers, { silent: false });
    } catch {
      // Already reported to the user via toast inside saveAnswers.
    }
  };

  const handleSubmit = async () => {
    if (!filledDataset) return;
    const missingRequired = getMissingRequired();
    if (missingRequired.length > 0) {
      toast.error(`Please answer all required questions (${missingRequired.length} remaining)`);
      setShowSubmitDialog(false);
      return;
    }

    setSubmitting(true);
    let savedOk = false;
    try {
      // Save suppresses its own toast here so a failure produces one accurate
      // message below rather than two stacked, neither of which says the
      // submit was skipped.
      await saveAnswers(answers, { silent: false, suppressErrorToast: true });
      savedOk = true;
      await onboardingAPI.submitFilledDataset(filledDataset.id);
      dirtyIdsRef.current.clear();
      setUnsavedCount(0);
      toast.success('Questionnaire marked as complete');
      navigate('/onboarding/my-progress');
    } catch (error) {
      console.error('Failed to submit:', error);
      toast.error(
        savedOk
          ? 'Failed to submit questionnaire'
          : 'Could not save your answers, so the questionnaire was not submitted. Your answers are still here — please try again.',
      );
    } finally {
      setSubmitting(false);
      setShowSubmitDialog(false);
    }
  };

  // CR-6165: debounced auto-save. Disabled while the form is read-only, while
  // a manual save or submit is running, and until the user has actually edited
  // something (otherwise hydrating `answers` on load would POST for every
  // visitor).
  const autoSaveEnabled = !!filledDataset && !isReadOnly && unsavedCount > 0 && !saving && !submitting;

  useAutoSave(
    answers,
    useCallback(
      async (value: Record<number, AnswerValue>) => { await saveAnswers(value, { silent: true }); },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [filledDataset?.id],
    ),
    AUTO_SAVE_DELAY_MS,
    autoSaveEnabled,
  );

  useEffect(() => {
    if (unsavedCount <= 0) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [unsavedCount]);

  return {
    navigate,
    loading,
    saving,
    submitting,
    filledDataset,
    answers,
    showSubmitDialog,
    setShowSubmitDialog,
    expandedSections,
    setExpandedSections,
    uploadingFiles,
    uploadedFiles,
    carriedForwardIds,
    carriedFromLabel,
    scorePreview,
    loadingScore,

    // CR-6165 auto-save
    autoSaveStatus,
    lastSavedAt,
    unsavedCount,

    // Derived
    isReadOnly,
    resubmissionMode,
    shouldShowSection,
    shouldShowQuestion,
    getMissingRequiredQuestions: getMissingRequired,
    getAnsweredCount: getAnswered,
    getTotalQuestions,

    // Actions
    handleAnswerChange,
    handleFileUpload,
    handleFileRemove,
    handleSave,
    handleSubmit,
  };
}
